import { useState } from "react";
import { Check, X, Copy } from "lucide-react";
import { toast } from "sonner";
import { approveCreator, declineCreator, formatApiError } from "@/lib/admin-api";

const FOLLOWERS = { "under-5k": "Under 5k", "5k-20k": "5k–20k", "20k-100k": "20k–100k", "100k-plus": "100k+" };
const BADGE = { pending: "bg-[#F4DED3] text-[#9E4429]", approved: "bg-[#DDE8DF] text-[#3F5E57]", declined: "bg-[#EEE7E3] text-[#6B5A52]" };

const Row = ({ r, onChange }) => {
  const [busy, setBusy] = useState(false);
  const act = async (fn, msg) => {
    setBusy(true);
    try { await fn(r.id); toast.success(msg); onChange(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const copy = () => navigator.clipboard.writeText(`${window.location.origin}/c/${r.code}`).then(() => toast.success("Invite link copied")).catch(() => {});
  return (
    <tr className="border-t border-[#C86D51]/10 align-top text-[#2C221E]" data-testid={`admin-creator-row-${r.id}`}>
      <td className="px-4 py-3"><p className="font-semibold">{r.name}</p><p className="text-xs text-[#6B5A52]">{r.email}</p></td>
      <td className="px-4 py-3">
        {r.link ? <a href={r.link} target="_blank" rel="noopener noreferrer" className="text-[#9E4429] underline underline-offset-2">@{r.handle}</a> : <span>@{r.handle}</span>}
        <p className="text-xs capitalize text-[#6B5A52]">{r.platform} · {FOLLOWERS[r.followers] ?? r.followers}</p>
      </td>
      <td className="max-w-[240px] px-4 py-3 text-xs text-[#5C4A42]">{r.niche}{r.message && <p className="mt-1 italic">"{r.message}"</p>}</td>
      <td className="px-4 py-3"><span className={`rounded-full px-2.5 py-1 text-xs font-semibold capitalize ${BADGE[r.status]}`} data-testid={`admin-creator-status-${r.id}`}>{r.status}</span></td>
      <td className="px-4 py-3">
        {r.code ? <button type="button" onClick={copy} className="inline-flex items-center gap-1.5 font-mono text-sm" data-testid={`admin-creator-code-${r.id}`}>{r.code}<Copy className="h-3.5 w-3.5 text-[#9E4429]" /></button> : <span className="text-[#8a766c]">—</span>}
      </td>
      <td className="px-4 py-3 font-serif text-lg" data-testid={`admin-creator-signups-${r.id}`}>{r.signups}<span className="ml-1 font-sans text-xs text-[#6B5A52]">({r.confirmed} confirmed)</span></td>
      <td className="px-4 py-3">
        <div className="flex gap-2">
          {r.status !== "approved" && <button type="button" disabled={busy} onClick={() => act(approveCreator, "Approved. Invite code emailed.")} data-testid={`admin-creator-approve-${r.id}`} className="inline-flex h-9 items-center gap-1 rounded-full bg-[#2C221E] px-3 text-xs font-semibold text-[#FBF5EE] hover:bg-[#3F5E57] disabled:opacity-60"><Check className="h-3.5 w-3.5" />Approve</button>}
          {r.status !== "declined" && <button type="button" disabled={busy} onClick={() => act(declineCreator, "Declined")} data-testid={`admin-creator-decline-${r.id}`} className="inline-flex h-9 items-center gap-1 rounded-full border border-[#C86D51]/25 px-3 text-xs font-semibold text-[#5C4A42] hover:border-[#9E4429] disabled:opacity-60"><X className="h-3.5 w-3.5" />{r.status === "approved" ? "Revoke" : "Decline"}</button>}
        </div>
      </td>
    </tr>
  );
};

export const CreatorsTable = ({ rows, onChange }) => (
  <div className="overflow-x-auto rounded-[1.5rem] border border-[#C86D51]/15 bg-white/70">
    <table className="w-full min-w-[900px] text-left text-sm" data-testid="admin-creators-table">
      <thead className="text-[11px] uppercase tracking-[0.16em] text-[#7A665C]">
        <tr>{["Creator", "Handle", "Niche", "Status", "Code", "Sign-ups", ""].map((h) => <th key={h} className="px-4 py-3 font-semibold">{h}</th>)}</tr>
      </thead>
      <tbody>
        {rows.map((r) => <Row key={r.id} r={r} onChange={onChange} />)}
        {!rows.length && <tr><td colSpan={7} className="px-4 py-10 text-center text-[#6B5A52]" data-testid="admin-creators-empty">No creator applications yet. Share /creators with creators you'd love to work with.</td></tr>}
      </tbody>
    </table>
  </div>
);
